import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Languages, Landmark, BookOpen, Users, ChevronDown, BookOpenCheck
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { SectionHeader, PremiumBadge } from './SettingsHelpers';
import CollapsibleSettingsCard from '@/components/CollapsibleSettingsCard';
import {
  mainTranslations,
  glassCard, glassOption
} from './settingsConstants';

export function StudyToolsTab({
  selectedTranslations, toggleTranslation,
  greekAnalysisEnabled, setGreekAnalysisEnabled,
  historicalContextEnabled, setHistoricalContextEnabled,
  deepStudyEnabled, setDeepStudyEnabled,
  simpleLanguageEnabled, setSimpleLanguageEnabled,
  selectedCommentators, toggleCommentator, setSelectedCommentators, commentators,
  commentaryExpanded, setCommentaryExpanded,
  isPaid, setUpgradeFeature, updateSetting,
}) {
  const { t } = useTranslation();

  return (
    <>
      {/* Bible Translations */}
      <CollapsibleSettingsCard
        storageKey="translations"
        tabId="study"
        searchTerms="translations bible kjv esv niv nasb amp asv web ylt rvr lbla almeida"
        glassCard={glassCard}
        testId="settings-translations-section"
        defaultOpen={false}
        icon={<Languages className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />}
        title={t('settings.translations')}
        subtitle={`${selectedTranslations.length} selected`}
      >
        <p className="text-sm mb-4" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.translations_desc')}</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" style={{ fontSize: '16px' }}>
          {mainTranslations.map((transId) => {
            const selected = selectedTranslations.includes(transId);
            return (
              <button
                key={transId}
                onClick={() => toggleTranslation(transId)}
                className="py-3 px-1 rounded-xl text-center transition-all overflow-hidden"
                style={{ ...glassOption(selected), fontSize: '13px', fontWeight: 500 }}
                data-testid={`settings-translation-${transId}`}
                title={t(`bible_trans.${transId}`, transId.toUpperCase())}
              >
                <span className="block truncate" style={{ color: selected ? 'var(--theme-text)' : 'var(--theme-textSecondary)' }}>
                  {transId.toUpperCase()}
                </span>
              </button>
            );
          })}
        </div>
      </CollapsibleSettingsCard>
      {/* Easy to Read Mode */}
      <div className="rounded-3xl p-6" style={glassCard} data-testid="settings-easy-read-section">
        <div className="flex items-center gap-3 mb-5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'var(--glass-bg-active)' }}
          >
            <BookOpenCheck className="h-[18px] w-[18px]" style={{ color: '#ec4899' }} />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>{t('tools.easy_read', 'Easy to Read')}</h2>
        </div>
        <div
          className="p-4 rounded-2xl transition-all"
          style={glassOption(simpleLanguageEnabled)}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <span className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                {t('tools.easy_read', 'Easy to Read')}
              </span>
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('tools.easy_read_desc', 'Simplified language for clearer understanding')}
              </p>
            </div>
            <Switch
              checked={simpleLanguageEnabled}
              onCheckedChange={(checked) => {
                setSimpleLanguageEnabled(checked);
                updateSetting('simple_language', checked);
              }}
              data-testid="settings-easy-read-toggle"
            />
          </div>
        </div>
      </div>
      {/* Original Language - visible but greyed out for free */}
      <div className={`rounded-3xl p-6 relative ${!isPaid ? 'opacity-40' : ''}`} style={glassCard} data-testid="settings-language-tools-section">
        {!isPaid && (
          <div className="absolute inset-0 rounded-3xl z-10 cursor-pointer" onClick={() => setUpgradeFeature('greek_analysis')} data-testid="greek-upgrade-overlay" />
        )}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--glass-bg-active)' }}>
            <Languages className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>{t('settings.original_language', 'Greek Analysis')}</h2>
          {!isPaid && <PremiumBadge />}
        </div>
        <div className="p-4 rounded-2xl transition-all" style={glassOption(greekAnalysisEnabled)}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <span className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{t('settings.greek_analysis')}</span>
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.greek_analysis_desc')}</p>
            </div>
            <Switch
              checked={greekAnalysisEnabled}
              onCheckedChange={(checked) => { setGreekAnalysisEnabled(checked); updateSetting('greek_analysis_enabled', checked); }}
              data-testid="settings-greek-toggle"
            />
          </div>
        </div>
      </div>
      {/* Historical & Cultural Context */}
      <div className={`rounded-3xl p-6 relative ${!isPaid ? 'opacity-40' : ''}`} style={glassCard} data-testid="settings-historical-context-section">
        {!isPaid && (
          <div className="absolute inset-0 rounded-3xl z-10 cursor-pointer" onClick={() => setUpgradeFeature('historical_context')} data-testid="historical-context-upgrade-overlay" />
        )}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--glass-bg-active)' }}>
            <Landmark className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>{t('settings.historical_context', 'Historical Context')}</h2>
          {!isPaid && <PremiumBadge />}
        </div>
        <div className="p-4 rounded-2xl transition-all" style={glassOption(historicalContextEnabled)}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <span className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{t('settings.historical_context_label', 'Historical & Cultural Context')}</span>
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.historical_context_desc', 'Include researched background on the historical setting, cultural practices, and authorial context of passages')}</p>
            </div>
            <Switch
              checked={historicalContextEnabled}
              onCheckedChange={(checked) => { setHistoricalContextEnabled(checked); updateSetting('historical_context_enabled', checked); }}
              data-testid="settings-historical-context-toggle"
            />
          </div>
        </div>
      </div>
      {/* Deep Study Mode */}
      <div className={`rounded-3xl p-6 relative ${!isPaid ? 'opacity-40' : ''}`} style={glassCard} data-testid="settings-deep-study-section">
        {!isPaid && (
          <div className="absolute inset-0 rounded-3xl z-10 cursor-pointer" onClick={() => setUpgradeFeature('deep_study')} data-testid="deep-study-upgrade-overlay" />
        )}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--glass-bg-active)' }}>
            <BookOpen className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.studytoolstab_deep_study_92a6')}</h2>
          {!isPaid && <PremiumBadge />}
        </div>
        <div className="p-4 rounded-2xl transition-all" style={glassOption(deepStudyEnabled)}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <span className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.studytoolstab_deep_study_mode_846b')}</span>
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.studytoolstab_get_more_comprehensive_99c1')}</p>
            </div>
            <Switch
              checked={deepStudyEnabled}
              onCheckedChange={(checked) => { setDeepStudyEnabled(checked); updateSetting('deep_study_enabled', checked); }}
              data-testid="settings-deep-study-toggle"
            />
          </div>
        </div>
      </div>
      {/* Commentary Library */}
      <div className={`rounded-3xl p-6 relative ${!isPaid ? 'opacity-40' : ''}`} style={glassCard} data-testid="settings-commentary-section">
        {!isPaid && (
          <div className="absolute inset-0 rounded-3xl z-10 cursor-pointer" onClick={() => setUpgradeFeature('commentators')} data-testid="commentary-upgrade-overlay" />
        )}
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => {
            const opening = !commentaryExpanded;
            setCommentaryExpanded(opening);
            // Auto-select defaults when paid user first opens with no selections
            if (opening && isPaid && selectedCommentators.length === 0 && commentators.length > 0) {
              const defaults = commentators.slice(0, 3).map(c => c.id);
              setSelectedCommentators(defaults);
              updateSetting('selected_commentators', defaults);
              updateSetting('commentators_enabled', true);
            }
          }}
          data-testid="settings-commentary-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--glass-bg-active)' }}>
              <Users className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>{t('settings.commentary')}</h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{selectedCommentators.length} selected</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isPaid && <PremiumBadge />}
            <ChevronDown
              className="h-5 w-5 transition-transform duration-300"
              style={{ color: 'var(--theme-textSecondary)', transform: commentaryExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </div>
        </div>
        <div
          className="overflow-hidden transition-all duration-300"
          style={{ maxHeight: commentaryExpanded ? '320px' : '0', opacity: commentaryExpanded ? 1 : 0, marginTop: commentaryExpanded ? '16px' : '0' }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>{t('settings.commentary_desc')}</p>
            {isPaid && (
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedCommentators([]); updateSetting('selected_commentators', []); updateSetting('commentators_enabled', false); }}
                className="text-xs font-medium shrink-0 ml-3"
                style={{ color: 'var(--theme-textSecondary)' }}
                data-testid="settings-deselect-commentators"
              >
                {t('settings.deselect_all', 'Deselect All')}
              </button>
            )}
          </div>
          <div className="space-y-2.5 overflow-y-auto max-h-[240px] pr-1">
            {commentators.map((comm) => {
              const selected = selectedCommentators.includes(comm.id);
              return (
                <div
                  key={comm.id}
                  onClick={() => toggleCommentator(comm.id)}
                  className="p-4 rounded-2xl cursor-pointer transition-all"
                  style={glassOption(selected)}
                  data-testid={`settings-commentator-${comm.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox checked={selected} onCheckedChange={() => toggleCommentator(comm.id)} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{t(`commentator_info.${comm.id}.name`, comm.name)}</h3>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t(`commentator_info.${comm.id}.desc`, comm.desc)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Weekly Recap and Daily Reflection notifications moved to Preferences tab */}
    </>
  );
}
